/**
 * RMH Theme Main JavaScript
 */

import './main.css';
import 'sharer.js';
import './share.js';
import './stories-filter.js';
import './programs-filter.js';
import './events-filter.js';
import './press-filter.js';
import './story-single.js';

// DOM Ready
document.addEventListener('DOMContentLoaded', () => {
	initMobileMenu();
	initScrollHeader();
	initStickyTabs();
	initDropdowns();
	initDesktopSearch();
	initLanguageDropdowns();
});

/**
 * Mobile menu toggle.
 *
 * Opens/closes the off-canvas drawer and swaps the hamburger/close icons.
 */
function initMobileMenu() {
	const toggle = document.getElementById('mobile-menu-toggle');
	const drawer = document.getElementById('mobile-menu');

	if (!toggle || !drawer) {
		return;
	}

	const iconOpen = toggle.querySelector('.rmh-header__icon-open');
	const iconClose = toggle.querySelector('.rmh-header__icon-close');
	const labelOpen = toggle.dataset.labelOpen || 'Open menu';
	const labelClose = toggle.dataset.labelClose || 'Close menu';

	drawer.toggleAttribute('inert', drawer.getAttribute('aria-hidden') === 'true');

	toggle.addEventListener('click', () => {
		const isOpen = drawer.getAttribute('aria-hidden') === 'false';

		drawer.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
		drawer.toggleAttribute('inert', isOpen);
		toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
		toggle.setAttribute('aria-label', isOpen ? labelOpen : labelClose);

		if (iconOpen && iconClose) {
			iconOpen.classList.toggle('hidden', !isOpen);
			iconClose.classList.toggle('hidden', isOpen);
		}

		// Prevent body scroll when drawer is open.
		document.body.style.overflow = isOpen ? '' : 'hidden';
	});

	// Close drawer on escape key.
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
			toggle.click();
			toggle.focus();
		}
	});
}

/**
 * Scroll-aware header state.
 *
 * Switches between "primary" (full) and "scroll" (compact) states
 * based on scroll position. Uses a 50px threshold to avoid flicker.
 */
function initScrollHeader() {
	const header = document.getElementById('masthead');

	if (!header) {
		return;
	}

	const SCROLL_THRESHOLD = 50;
	let ticking = false;

	// Sticky elements (e.g. tabs block) anchor to --rmh-header-height. The static
	// CSS token is per-breakpoint, but the header compresses on scroll, so write
	// the live offsetHeight to keep stickies flush with the bottom of the header.
	function syncHeaderHeight() {
		document.documentElement.style.setProperty(
			'--rmh-header-height',
			header.offsetHeight + 'px'
		);
	}

	// Anchor jumps land deep in the page, where the header is always compact,
	// so scroll-padding-top uses the compact height rather than the live one.
	// Otherwise a jump from the top of the page clears the full header and the
	// target sits too low once the header compresses.
	function syncCompactHeight() {
		document.documentElement.style.setProperty(
			'--rmh-header-compact-height',
			measureInState(header, 'data-header-state', 'scroll') + 'px'
		);
	}

	function update() {
		const scrolled = window.scrollY > SCROLL_THRESHOLD;
		const current = header.getAttribute('data-header-state');
		const next = scrolled ? 'scroll' : 'primary';

		if (current !== next) {
			header.setAttribute('data-header-state', next);
		}

		syncHeaderHeight();
		ticking = false;
	}

	window.addEventListener('scroll', () => {
		if (!ticking) {
			requestAnimationFrame(update);
			ticking = true;
		}
	}, { passive: true });

	window.addEventListener('resize', () => {
		syncHeaderHeight();
		syncCompactHeight();
	}, { passive: true });

	// `data-header-state` flips trigger a 300ms padding transition. Without
	// this, the CSS var would lag behind the actual header height through the
	// transition (and never update if the user stops scrolling exactly at the
	// flip), leaving sticky elements briefly misaligned. ResizeObserver fires
	// on every box-size change, including transitioning padding.
	if ('ResizeObserver' in window) {
		const ro = new ResizeObserver(syncHeaderHeight);
		ro.observe(header);
	}

	syncHeaderHeight();
	syncCompactHeight();
	update();
}

/**
 * Sticky tabs: detect when a .rmh-tabs section pins to the top so CSS can
 * collapse its padding (`[data-stuck]`, set as a valueless attribute). Checks
 * the position of a 1px sentinel above the section on scroll.
 *
 * The collapse makes the section shorter, and because a sticky element stays
 * in the document flow, everything after it would move up. Browsers with
 * scroll anchoring then pull the scroll position back by the same amount,
 * which is more than the hysteresis band below, so the section unsticks,
 * grows, re-sticks, and bounces (RMH-431). A spacer after the section grows
 * by exactly the height the section loses, so the flow height never changes.
 */
function initStickyTabs() {
	const sections = document.querySelectorAll('.rmh-tabs');
	if (!sections.length) {
		return;
	}

	// Hysteresis band. Sticky kicks on when the sentinel scrolls above one line
	// (24px from the viewport top) and only releases when it comes back below a
	// second, lower line (40px). The 16px gap absorbs tiny scroll wobble at the
	// boundary so the layout doesn't visibly stutter.
	const STICK_LINE = 24;
	const RELEASE_LINE = 40;

	const sentinels = new Map();
	const spacers = new Map();
	let ticking = false;

	// Measure each section in both states (without animating) to size its
	// spacer. Anchor jumps also need scroll-padding-top to clear the sticky
	// tabs bar, and the bar is always stuck after a jump, so publish the
	// tallest stuck height for the html-level scroll-padding rule.
	const measureTabs = () => {
		let tallestStuck = 0;
		sections.forEach((section) => {
			const unstuck = measureInState(section, 'data-stuck', null);
			const stuck = measureInState(section, 'data-stuck', '');
			spacers.get(section).style.setProperty('--rmh-tabs-collapse', Math.max(0, unstuck - stuck) + 'px');
			tallestStuck = Math.max(tallestStuck, stuck);
		});
		document.documentElement.style.setProperty('--rmh-sticky-tabs-height', tallestStuck + 'px');
	};

	// Compares positions rather than waiting for a crossing (as an
	// IntersectionObserver would), so an anchor jump that skips straight past
	// the section, or a page load deep in the page, still lands in the right
	// state. Reads every sentinel before writing any attribute to avoid
	// forcing layout between sections.
	const updateStuck = () => {
		ticking = false;
		const tops = Array.from(sections, (section) => sentinels.get(section).getBoundingClientRect().top);
		sections.forEach((section, i) => {
			const isStuck = section.hasAttribute('data-stuck');
			if (!isStuck && tops[i] < STICK_LINE) {
				section.setAttribute('data-stuck', '');
			} else if (isStuck && tops[i] >= RELEASE_LINE) {
				section.removeAttribute('data-stuck');
			}
		});
	};

	sections.forEach((section) => {
		// Sentinel sits just above the section to detect when the section pins.
		// The negative margin keeps it out of the document flow so it doesn't
		// add a 1px gap between the previous block and the sticky section.
		const sentinel = document.createElement('div');
		sentinel.setAttribute('aria-hidden', 'true');
		sentinel.style.cssText = 'height:1px;width:100%;margin-bottom:-1px;pointer-events:none;';
		section.parentNode.insertBefore(sentinel, section);
		sentinels.set(section, sentinel);

		// Spacer sits just after the section. Zero height until the section is
		// stuck; see the `.rmh-tabs__spacer` rule in components.css.
		const spacer = document.createElement('div');
		spacer.className = 'rmh-tabs__spacer';
		spacer.setAttribute('aria-hidden', 'true');
		section.after(spacer);
		spacers.set(section, spacer);
	});

	window.addEventListener('scroll', () => {
		if (!ticking) {
			requestAnimationFrame(updateStuck);
			ticking = true;
		}
	}, { passive: true });

	// Resizes and late web fonts can reflow content above the tabs and move the
	// sentinel across a line without firing a scroll event, so re-check the
	// stuck state after every remeasure.
	const refresh = () => {
		measureTabs();
		updateStuck();
	};

	refresh();
	window.addEventListener('resize', refresh, { passive: true });
	// Web fonts can change the tab buttons' line boxes after first paint.
	if (document.fonts) {
		document.fonts.ready.then(refresh);
	}
}

/**
 * Read an element's height with `attr` set to `value` (or removed, when
 * `value` is null), without animating into that state. Transitions are
 * suppressed for the element and its descendants while the state is applied,
 * measured, and restored.
 */
function measureInState(el, attr, value) {
	const prev = el.getAttribute(attr);

	el.classList.add('rmh-no-transition');
	if (value === null) {
		el.removeAttribute(attr);
	} else {
		el.setAttribute(attr, value);
	}
	const height = el.offsetHeight;

	if (prev === null) {
		el.removeAttribute(attr);
	} else {
		el.setAttribute(attr, prev);
	}
	// Flush the restored styles before transitions come back so nothing animates.
	void el.offsetHeight;
	el.classList.remove('rmh-no-transition');

	return height;
}

/**
 * Navigation dropdown and mobile submenu behavior.
 *
 * Desktop parent items are normal links; their dropdowns open on hover/focus.
 * On mobile, the dedicated chevron buttons toggle the submenu panels.
 */
function initDropdowns() {
	const submenuToggles = document.querySelectorAll('.nav-submenu-toggle');

	submenuToggles.forEach((btn) => {
		btn.addEventListener('click', (e) => {
			e.preventDefault();
			const isExpanded = btn.getAttribute('aria-expanded') === 'true';

			// Close all other open mobile submenus first.
			submenuToggles.forEach((other) => {
				if (other !== btn) {
					other.setAttribute('aria-expanded', 'false');
				}
			});

			btn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
		});
	});

	// Close dropdowns when clicking outside.
	document.addEventListener('click', (e) => {
		if (!e.target.closest('.nav-item-has-children')) {
			submenuToggles.forEach((btn) => {
				btn.setAttribute('aria-expanded', 'false');
			});
		}
	});

	// Close dropdowns on Escape.
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			submenuToggles.forEach((btn) => {
				if (btn.getAttribute('aria-expanded') === 'true') {
					btn.setAttribute('aria-expanded', 'false');
					btn.focus();
				}
			});
		}
	});
}

/**
 * Desktop search dropdown toggle.
 */
function initDesktopSearch() {
	const toggle = document.getElementById('desktop-search-toggle');
	const panel = document.getElementById('desktop-search-panel');

	if (!toggle || !panel) {
		return;
	}

	const searchField = panel.querySelector('#desktop-search-field');
	const clearButton = panel.querySelector('#desktop-search-clear');
	const labelOpen = toggle.dataset.labelOpen || 'Open search';
	const labelClose = toggle.dataset.labelClose || 'Close search';

	function updateClearButton() {
		if (!searchField || !clearButton) {
			return;
		}

		clearButton.classList.toggle('hidden', searchField.value.length === 0);
	}

	function openPanel() {
		panel.hidden = false;
		panel.setAttribute('aria-hidden', 'false');
		toggle.setAttribute('aria-expanded', 'true');
		toggle.setAttribute('aria-label', labelClose);

		requestAnimationFrame(() => {
			panel.classList.add('is-open');
			updateClearButton();
			searchField?.focus();
		});
	}

	function closePanel(restoreFocus = false, immediate = false) {
		panel.classList.remove('is-open');
		panel.setAttribute('aria-hidden', 'true');
		toggle.setAttribute('aria-expanded', 'false');
		toggle.setAttribute('aria-label', labelOpen);

		if (immediate) {
			panel.hidden = true;
		} else {
			window.setTimeout(() => {
				if (panel.getAttribute('aria-hidden') === 'true') {
					panel.hidden = true;
				}
			}, 200);
		}

		if (restoreFocus) {
			toggle.focus();
		}
	}

	toggle.addEventListener('click', () => {
		const isOpen = toggle.getAttribute('aria-expanded') === 'true';

		if (isOpen) {
			closePanel(true);
		} else {
			openPanel();
		}
	});

	document.addEventListener('click', (event) => {
		if (panel.hidden) {
			return;
		}

		if (!panel.contains(event.target) && !toggle.contains(event.target)) {
			closePanel(false);
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
			closePanel(true);
		}
	});

	window.addEventListener('resize', () => {
		if (window.innerWidth < 1120 && toggle.getAttribute('aria-expanded') === 'true') {
			closePanel(false, true);
		}
	});

	searchField?.addEventListener('input', updateClearButton);

	clearButton?.addEventListener('click', () => {
		if (!searchField) {
			return;
		}

		searchField.value = '';
		updateClearButton();
		searchField.focus();
	});

	updateClearButton();
}

/**
 * Language dropdown toggle.
 *
 * Each [data-lang-dropdown] wrapper contains a <button> trigger and
 * a [data-lang-panel] panel. Clicking the button toggles the panel;
 * clicking outside or pressing Escape closes it.
 */
function initLanguageDropdowns() {
	const wrappers = document.querySelectorAll('[data-lang-dropdown]');

	if (!wrappers.length) {
		return;
	}

	wrappers.forEach((wrapper) => {
		const btn = wrapper.querySelector('button');
		const panel = wrapper.querySelector('[data-lang-panel]');

		if (!btn || !panel) {
			return;
		}

		btn.addEventListener('click', () => {
			const isOpen = btn.getAttribute('aria-expanded') === 'true';

			// Close all other language dropdowns first.
			wrappers.forEach((other) => {
				if (other !== wrapper) {
					const otherBtn = other.querySelector('button');
					const otherPanel = other.querySelector('[data-lang-panel]');
					otherBtn?.setAttribute('aria-expanded', 'false');
					otherPanel?.classList.add('hidden');
					otherPanel?.setAttribute('aria-hidden', 'true');
				}
			});

			btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
			panel.classList.toggle('hidden', isOpen);
			panel.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
		});
	});

	// Close on outside click.
	document.addEventListener('click', (e) => {
		if (!e.target.closest('[data-lang-dropdown]')) {
			wrappers.forEach((wrapper) => {
				const btn = wrapper.querySelector('button');
				const panel = wrapper.querySelector('[data-lang-panel]');
				btn?.setAttribute('aria-expanded', 'false');
				panel?.classList.add('hidden');
				panel?.setAttribute('aria-hidden', 'true');
			});
		}
	});

	// Close on Escape.
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			wrappers.forEach((wrapper) => {
				const btn = wrapper.querySelector('button');
				const panel = wrapper.querySelector('[data-lang-panel]');

				if (btn?.getAttribute('aria-expanded') === 'true') {
					btn.setAttribute('aria-expanded', 'false');
					panel?.classList.add('hidden');
					panel?.setAttribute('aria-hidden', 'true');
					btn.focus();
				}
			});
		}
	});
}
